import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Divider,
  LinearProgress,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Tabs,
  Tab
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Speed as SpeedIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Timer as TimerIcon,
  Assessment as AssessmentIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Timeline as TimelineIcon,
  Memory as MemoryIcon,
  Storage as StorageIcon,
  CloudUpload as ApiIcon,
  Psychology as AiIcon,
  BugReport as BugIcon,
  Stars as StarsIcon,
  FlashOn as FlashIcon,
  Code as CodeIcon,
  Security as SecurityIcon,
  VerifiedUser as VerifiedIcon
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ComposedChart
} from 'recharts';
import { useAuthToken } from "../TokenContext";
import { useSelector } from "react-redux";
import { useTheme } from '@mui/material';

const COLORS = ['#00C49F', '#0088FE', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d'];

const AgentPerformanceMetrics = ({ collapsed }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('7d');
  const [selectedAgent, setSelectedAgent] = useState('all');
  const [selectedTab, setSelectedTab] = useState(0);
  const { user } = useSelector(state => state.auth);
  const token = useAuthToken();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  // Performance Data States
  const [overviewMetrics, setOverviewMetrics] = useState(null);
  const [executionTrends, setExecutionTrends] = useState([]);
  const [agentPerformance, setAgentPerformance] = useState([]);
  const [errorAnalysis, setErrorAnalysis] = useState([]);
  const [resourceUsage, setResourceUsage] = useState([]);
  const [recentExecutions, setRecentExecutions] = useState([]);
  const [performanceComparison, setPerformanceComparison] = useState([]);
  const [codeMetrics, setCodeMetrics] = useState([]);
  const [codeQualityOverview, setCodeQualityOverview] = useState(null);
  const [languageDistribution, setLanguageDistribution] = useState([]);

  useEffect(() => {
    if (user?.data?.[0]?.client_code && token) {
      fetchPerformanceMetrics();
    }
  }, [user, token, timeRange, selectedAgent]);

  const fetchPerformanceMetrics = async () => {
    setLoading(true);
    setError('');
    
    try {
      // In production, this would fetch from your API
      // For now, using mock data to demonstrate the UI
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Mock Overview Metrics
      setOverviewMetrics({
        totalExecutions: 15847,
        successRate: 96.8,
        avgExecutionTime: 2.34,
        totalApiCalls: 42156,
        avgAccuracy: 94.2,
        costEfficiency: 87.5,
        activeAgents: 12,
        totalErrors: 508,
        trendsVsPrevious: {
          executions: 12.5,
          successRate: 2.3,
          executionTime: -8.7,
          apiCalls: 15.2
        }
      });

      // Mock Execution Trends (last 30 days)
      const trendData = [];
      for (let i = 29; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        trendData.push({
          date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          executions: Math.floor(Math.random() * 200) + 400,
          successful: Math.floor(Math.random() * 190) + 380,
          failed: Math.floor(Math.random() * 20) + 5,
          avgTime: (Math.random() * 2 + 1.5).toFixed(2),
          apiCalls: Math.floor(Math.random() * 500) + 1200
        });
      }
      setExecutionTrends(trendData);

      // Mock Agent Performance
      setAgentPerformance([
        {
          id: 1,
          name: 'Sprint Planning Bot',
          type: 'Scrum Master',
          executions: 2456,
          successRate: 98.5,
          avgTime: 1.87,
          accuracy: 96.5,
          apiCalls: 8234,
          errors: 37,
          uptime: 99.2,
          lastExecution: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()
        },
        {
          id: 2,
          name: 'Code Review Assistant',
          type: 'Coder',
          executions: 4532,
          successRate: 95.8,
          avgTime: 3.45,
          accuracy: 93.2,
          apiCalls: 15678,
          errors: 190,
          uptime: 98.7,
          lastExecution: new Date(Date.now() - 30 * 60 * 1000).toISOString()
        },
        {
          id: 3,
          name: 'Test Automation Bot',
          type: 'Tester',
          executions: 3245,
          successRate: 97.2,
          avgTime: 2.12,
          accuracy: 95.8,
          apiCalls: 9876,
          errors: 91,
          uptime: 99.5,
          lastExecution: new Date(Date.now() - 15 * 60 * 1000).toISOString()
        },
        {
          id: 4,
          name: 'Deployment Manager',
          type: 'DevOps',
          executions: 1876,
          successRate: 96.4,
          avgTime: 4.23,
          accuracy: 92.1,
          apiCalls: 5432,
          errors: 68,
          uptime: 98.9,
          lastExecution: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString()
        },
        {
          id: 5,
          name: 'Bug Tracker AI',
          type: 'Analyst',
          executions: 3738,
          successRate: 94.6,
          avgTime: 2.89,
          accuracy: 91.5,
          apiCalls: 11234,
          errors: 202,
          uptime: 97.8,
          lastExecution: new Date(Date.now() - 45 * 60 * 1000).toISOString()
        }
      ]);

      // Mock Error Analysis
      setErrorAnalysis([
        { type: 'API Timeout', count: 145, percentage: 28.5, severity: 'high' },
        { type: 'Invalid Response', count: 123, percentage: 24.2, severity: 'medium' },
        { type: 'Rate Limit', count: 98, percentage: 19.3, severity: 'medium' },
        { type: 'Auth Failed', count: 76, percentage: 15.0, severity: 'high' },
        { type: 'Network Error', count: 66, percentage: 13.0, severity: 'low' }
      ]);

      // Mock Resource Usage
      const resourceData = [];
      for (let i = 23; i >= 0; i--) {
        resourceData.push({
          hour: `${23 - i}:00`,
          cpu: Math.floor(Math.random() * 40) + 30,
          memory: Math.floor(Math.random() * 35) + 40,
          apiCalls: Math.floor(Math.random() * 200) + 100,
          tokens: Math.floor(Math.random() * 50000) + 30000
        });
      }
      setResourceUsage(resourceData);

      // Mock Recent Executions
      setRecentExecutions([
        {
          id: 'exec-1234',
          agent: 'Code Review Assistant',
          status: 'success',
          startTime: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
          duration: 2.45,
          accuracy: 94.5,
          apiCalls: 23,
          tokensUsed: 4532,
          task: 'Review PR #1234'
        },
        {
          id: 'exec-1235',
          agent: 'Sprint Planning Bot',
          status: 'success',
          startTime: new Date(Date.now() - 25 * 60 * 1000).toISOString(),
          duration: 1.87,
          accuracy: 97.2,
          apiCalls: 15,
          tokensUsed: 3421,
          task: 'Generate Sprint Plan'
        },
        {
          id: 'exec-1236',
          agent: 'Test Automation Bot',
          status: 'failed',
          startTime: new Date(Date.now() - 35 * 60 * 1000).toISOString(),
          duration: 0.56,
          accuracy: 0,
          apiCalls: 8,
          tokensUsed: 1234,
          task: 'Run Integration Tests',
          error: 'API Timeout'
        },
        {
          id: 'exec-1237',
          agent: 'Deployment Manager',
          status: 'success',
          startTime: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
          duration: 4.12,
          accuracy: 91.8,
          apiCalls: 34,
          tokensUsed: 6789,
          task: 'Deploy to Staging'
        },
        {
          id: 'exec-1238',
          agent: 'Bug Tracker AI',
          status: 'success',
          startTime: new Date(Date.now() - 55 * 60 * 1000).toISOString(),
          duration: 2.98,
          accuracy: 92.4,
          apiCalls: 19,
          tokensUsed: 4123,
          task: 'Analyze Bug Reports'
        }
      ]);

      // Mock Performance Comparison
      setPerformanceComparison([
        { metric: 'Speed', 'Sprint Bot': 98, 'Code Review': 75, 'Test Bot': 95, 'Deploy Mgr': 65, 'Bug Tracker': 85 },
        { metric: 'Accuracy', 'Sprint Bot': 97, 'Code Review': 93, 'Test Bot': 96, 'Deploy Mgr': 92, 'Bug Tracker': 92 },
        { metric: 'Reliability', 'Sprint Bot': 99, 'Code Review': 96, 'Test Bot': 98, 'Deploy Mgr': 97, 'Bug Tracker': 94 },
        { metric: 'Efficiency', 'Sprint Bot': 96, 'Code Review': 88, 'Test Bot': 94, 'Deploy Mgr': 85, 'Bug Tracker': 90 },
        { metric: 'Cost', 'Sprint Bot': 92, 'Code Review': 78, 'Test Bot': 90, 'Deploy Mgr': 75, 'Bug Tracker': 82 }
      ]);

      // Mock Code Metrics Overview
      setCodeQualityOverview({
        totalLinesOfCode: 125847,
        avgTestCoverage: 78.5,
        avgCodeQuality: 8.2,
        avgComplexity: 6.8,
        totalPullRequests: 342,
        codeReviewScore: 4.3,
        trendsVsPrevious: {
          linesOfCode: 18.2,
          testCoverage: 5.3,
          codeQuality: 12.5,
          complexity: -8.2
        }
      });

      // Mock Code Metrics by Agent
      setCodeMetrics([
        {
          id: 1,
          agent: 'Code Review Assistant',
          linesOfCode: 45234,
          testCoverage: 82.5,
          codeQualityScore: 8.5,
          cyclomaticComplexity: 5.8,
          maintainabilityIndex: 87.2,
          bugs: 12,
          codeSmells: 45,
          vulnerabilities: 3,
          duplications: 2.3,
          pullRequests: 156,
          avgReviewTime: 2.3
        },
        {
          id: 2,
          agent: 'Test Automation Bot',
          linesOfCode: 38562,
          testCoverage: 95.2,
          codeQualityScore: 9.1,
          cyclomaticComplexity: 4.2,
          maintainabilityIndex: 92.5,
          bugs: 5,
          codeSmells: 18,
          vulnerabilities: 1,
          duplications: 1.2,
          pullRequests: 89,
          avgReviewTime: 1.8
        },
        {
          id: 3,
          agent: 'Sprint Planning Bot',
          linesOfCode: 18423,
          testCoverage: 68.9,
          codeQualityScore: 7.8,
          cyclomaticComplexity: 7.5,
          maintainabilityIndex: 78.3,
          bugs: 18,
          codeSmells: 67,
          vulnerabilities: 5,
          duplications: 4.1,
          pullRequests: 52,
          avgReviewTime: 3.1
        },
        {
          id: 4,
          agent: 'Deployment Manager',
          linesOfCode: 15234,
          testCoverage: 71.3,
          codeQualityScore: 7.9,
          cyclomaticComplexity: 8.2,
          maintainabilityIndex: 75.8,
          bugs: 15,
          codeSmells: 52,
          vulnerabilities: 4,
          duplications: 3.5,
          pullRequests: 28,
          avgReviewTime: 4.2
        },
        {
          id: 5,
          agent: 'Bug Tracker AI',
          linesOfCode: 8394,
          testCoverage: 74.6,
          codeQualityScore: 8.1,
          cyclomaticComplexity: 6.3,
          maintainabilityIndex: 82.1,
          bugs: 8,
          codeSmells: 31,
          vulnerabilities: 2,
          duplications: 2.8,
          pullRequests: 17,
          avgReviewTime: 2.7
        }
      ]);

      // Mock Language Distribution
      setLanguageDistribution([
        { language: 'JavaScript', linesOfCode: 42156, percentage: 33.5 },
        { language: 'TypeScript', linesOfCode: 35847, percentage: 28.5 },
        { language: 'Python', linesOfCode: 25234, percentage: 20.0 },
        { language: 'Java', linesOfCode: 15234, percentage: 12.1 },
        { language: 'Go', linesOfCode: 7376, percentage: 5.9 }
      ]);

    } catch (err) {
      console.error('Error fetching performance metrics:', err);
      setError('Failed to load performance metrics');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const getTrendIcon = (value) => {
    if (value > 0) return <TrendingUpIcon fontSize="small" color="success" />;
    if (value < 0) return <TrendingDownIcon fontSize="small" color="error" />;
    return null;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'success';
      case 'failed': return 'error';
      case 'running': return 'warning';
      default: return 'default';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getQualityColor = (score) => {
    if (score >= 8) return 'success.main';
    if (score >= 6) return 'warning.main';
    return 'error.main';
  };

  const getComplexityColor = (complexity) => {
    if (complexity <= 5) return 'success';
    if (complexity <= 10) return 'warning';
    return 'error';
  };

  if (loading && !overviewMetrics) {
    return (
      <Box sx={{ 
        p: 3, 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: isDarkMode ? '#121212' : '#f5f5f5'
      }}>
        <Box textAlign="center">
          <CircularProgress size={60} />
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            Loading performance metrics...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 3, 
      width: '100vw', 
      bgcolor: isDarkMode ? '#121212' : '#f5f5f5',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight="600">
            <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Agent Performance Metrics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Real-time monitoring and analysis of agent operations
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              label="Time Range"
            >
              <MenuItem value="24h">Last 24 Hours</MenuItem>
              <MenuItem value="7d">Last 7 Days</MenuItem>
              <MenuItem value="30d">Last 30 Days</MenuItem>
              <MenuItem value="90d">Last 90 Days</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Refresh Data">
            <IconButton onClick={fetchPerformanceMetrics} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => console.log('Export metrics')}
          >
            Export Report
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Overview Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
            border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`
          }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Total Executions
                  </Typography>
                  <Typography variant="h4" fontWeight="600">
                    {formatNumber(overviewMetrics?.totalExecutions || 0)}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                    {getTrendIcon(overviewMetrics?.trendsVsPrevious?.executions)}
                    <Typography 
                      variant="caption" 
                      color={overviewMetrics?.trendsVsPrevious?.executions > 0 ? 'success.main' : 'error.main'}
                    >
                      {Math.abs(overviewMetrics?.trendsVsPrevious?.executions || 0)}% vs last period
                    </Typography>
                  </Box>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                  <FlashIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
            border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`
          }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Success Rate
                  </Typography>
                  <Typography variant="h4" fontWeight="600" color="success.main">
                    {overviewMetrics?.successRate || 0}%
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                    {getTrendIcon(overviewMetrics?.trendsVsPrevious?.successRate)}
                    <Typography 
                      variant="caption" 
                      color={overviewMetrics?.trendsVsPrevious?.successRate > 0 ? 'success.main' : 'error.main'}
                    >
                      {Math.abs(overviewMetrics?.trendsVsPrevious?.successRate || 0)}% vs last period
                    </Typography>
                  </Box>
                </Box>
                <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}>
                  <SuccessIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
            border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`
          }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Avg Execution Time
                  </Typography>
                  <Typography variant="h4" fontWeight="600">
                    {overviewMetrics?.avgExecutionTime || 0}s
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                    {getTrendIcon(overviewMetrics?.trendsVsPrevious?.executionTime)}
                    <Typography 
                      variant="caption" 
                      color={overviewMetrics?.trendsVsPrevious?.executionTime < 0 ? 'success.main' : 'error.main'}
                    >
                      {Math.abs(overviewMetrics?.trendsVsPrevious?.executionTime || 0)}% vs last period
                    </Typography>
                  </Box>
                </Box>
                <Avatar sx={{ bgcolor: 'info.main', width: 56, height: 56 }}>
                  <SpeedIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%',
            bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
            border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`
          }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Avg Accuracy
                  </Typography>
                  <Typography variant="h4" fontWeight="600" color="primary.main">
                    {overviewMetrics?.avgAccuracy || 0}%
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                    <StarsIcon fontSize="small" color="primary" />
                    <Typography variant="caption" color="text.secondary">
                      Across all agents
                    </Typography>
                  </Box>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.main', width: 56, height: 56 }}>
                  <AiIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Secondary Metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ 
            p: 2,
            bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
            border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`
          }}>
            <Box display="flex" alignItems="center" gap={2}>
              <ApiIcon color="primary" fontSize="large" />
              <Box>
                <Typography variant="caption" color="text.secondary">Total API Calls</Typography>
                <Typography variant="h6" fontWeight="600">
                  {formatNumber(overviewMetrics?.totalApiCalls || 0)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ 
            p: 2,
            bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
            border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`
          }}>
            <Box display="flex" alignItems="center" gap={2}>
              <TimerIcon color="success" fontSize="large" />
              <Box>
                <Typography variant="caption" color="text.secondary">Active Agents</Typography>
                <Typography variant="h6" fontWeight="600">
                  {overviewMetrics?.activeAgents || 0}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ 
            p: 2,
            bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
            border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`
          }}>
            <Box display="flex" alignItems="center" gap={2}>
              <BugIcon color="error" fontSize="large" />
              <Box>
                <Typography variant="caption" color="text.secondary">Total Errors</Typography>
                <Typography variant="h6" fontWeight="600">
                  {overviewMetrics?.totalErrors || 0}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ 
            p: 2,
            bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
            border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`
          }}>
            <Box display="flex" alignItems="center" gap={2}>
              <MemoryIcon color="warning" fontSize="large" />
              <Box>
                <Typography variant="caption" color="text.secondary">Cost Efficiency</Typography>
                <Typography variant="h6" fontWeight="600">
                  {overviewMetrics?.costEfficiency || 0}%
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabs for Different Views */}
      <Paper sx={{ 
        mb: 3,
        bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
        border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`
      }}>
        <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="Execution Trends" />
          <Tab label="Agent Performance" />
          <Tab label="Resource Usage" />
          <Tab label="Error Analysis" />
          <Tab label="Code Metrics" icon={<CodeIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {selectedTab === 0 && (
        <>
          {/* Execution Trends Chart */}
          <Paper sx={{ 
            p: 3, 
            mb: 3,
            bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
            border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`
          }}>
            <Typography variant="h6" gutterBottom fontWeight="600">
              <TimelineIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Execution Trends
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Daily execution statistics over the selected time period
            </Typography>
            
            <ResponsiveContainer width="100%" height={400}>
              <AreaChart data={executionTrends}>
                <defs>
                  <linearGradient id="colorExecutions" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorSuccessful" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#00C49F" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#00C49F" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="executions" 
                  stroke="#8884d8" 
                  fillOpacity={1} 
                  fill="url(#colorExecutions)" 
                  name="Total Executions"
                />
                <Area 
                  type="monotone" 
                  dataKey="successful" 
                  stroke="#00C49F" 
                  fillOpacity={1} 
                  fill="url(#colorSuccessful)" 
                  name="Successful"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>

          {/* Execution Time Trend */}
          <Paper sx={{ 
            p: 3, 
            mb: 3,
            bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
            border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`
          }}>
            <Typography variant="h6" gutterBottom fontWeight="600">
              Average Execution Time
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Performance optimization over time
            </Typography>
            
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={executionTrends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="avgTime" 
                  stroke="#FF8042" 
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  name="Avg Time (seconds)"
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </>
      )}

      {selectedTab === 1 && (
        <>
          {/* Agent Performance Comparison */}
          <Paper sx={{ 
            p: 3, 
            mb: 3,
            bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
            border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`
          }}>
            <Typography variant="h6" gutterBottom fontWeight="600">
              Agent Performance Comparison
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Comparative analysis across different performance metrics
            </Typography>
            
            <ResponsiveContainer width="100%" height={400}>
              <RadarChart data={performanceComparison}>
                <PolarGrid />
                <PolarAngleAxis dataKey="metric" />
                <PolarRadiusAxis angle={90} domain={[0, 100]} />
                <Radar name="Sprint Bot" dataKey="Sprint Bot" stroke="#8884d8" fill="#8884d8" fillOpacity={0.3} />
                <Radar name="Code Review" dataKey="Code Review" stroke="#00C49F" fill="#00C49F" fillOpacity={0.3} />
                <Radar name="Test Bot" dataKey="Test Bot" stroke="#FFBB28" fill="#FFBB28" fillOpacity={0.3} />
                <Legend />
              </RadarChart>
            </ResponsiveContainer>
          </Paper>

          {/* Individual Agent Performance Table */}
          <Paper sx={{ 
            bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
            border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`
          }}>
            <Box sx={{ p: 2, borderBottom: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}` }}>
              <Typography variant="h6" fontWeight="600">
                Individual Agent Metrics
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: isDarkMode ? '#2c2c2c' : '#f8f9fa' }}>
                  <TableRow>
                    <TableCell>Agent Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="center">Executions</TableCell>
                    <TableCell align="center">Success Rate</TableCell>
                    <TableCell align="center">Avg Time</TableCell>
                    <TableCell align="center">Accuracy</TableCell>
                    <TableCell align="center">Uptime</TableCell>
                    <TableCell align="center">Errors</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {agentPerformance.map((agent) => (
                    <TableRow key={agent.id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="500">
                            {agent.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Last run: {formatDate(agent.lastExecution)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={agent.type} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="500">
                          {formatNumber(agent.executions)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box>
                          <Typography 
                            variant="body2" 
                            fontWeight="600"
                            color={agent.successRate >= 95 ? 'success.main' : 'warning.main'}
                          >
                            {agent.successRate}%
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={agent.successRate} 
                            sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                            color={agent.successRate >= 95 ? 'success' : 'warning'}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {agent.avgTime}s
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box>
                          <Typography variant="body2" fontWeight="500">
                            {agent.accuracy}%
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={agent.accuracy} 
                            sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                            color="primary"
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={`${agent.uptime}%`}
                          size="small"
                          color={agent.uptime >= 99 ? 'success' : 'warning'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={agent.errors}
                          size="small"
                          color={agent.errors < 100 ? 'success' : 'error'}
                          icon={<BugIcon />}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {selectedTab === 2 && (
        <>
          {/* Resource Usage Chart */}
          <Paper sx={{ 
            p: 3, 
            mb: 3,
            bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
            border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`
          }}>
            <Typography variant="h6" gutterBottom fontWeight="600">
              <StorageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Resource Usage Over Time
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              CPU, Memory, and API usage patterns (Last 24 hours)
            </Typography>
            
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={resourceUsage}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <RechartsTooltip />
                <Legend />
                <Line 
                  yAxisId="left"
                  type="monotone" 
                  dataKey="cpu" 
                  stroke="#8884d8" 
                  strokeWidth={2}
                  name="CPU Usage (%)"
                />
                <Line 
                  yAxisId="left"
                  type="monotone" 
                  dataKey="memory" 
                  stroke="#00C49F" 
                  strokeWidth={2}
                  name="Memory Usage (%)"
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="apiCalls" 
                  stroke="#FFBB28" 
                  strokeWidth={2}
                  name="API Calls"
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>

          {/* Token Usage */}
          <Paper sx={{ 
            p: 3,
            bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
            border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`
          }}>
            <Typography variant="h6" gutterBottom fontWeight="600">
              Token Usage Pattern
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              AI token consumption over time
            </Typography>
            
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={resourceUsage}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Bar dataKey="tokens" fill="#82ca9d" name="Tokens Used" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </>
      )}

      {selectedTab === 3 && (
        <Grid container spacing={3}>
          {/* Error Distribution */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ 
              p: 3,
              bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
              border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`,
              height: '100%'
            }}>
              <Typography variant="h6" gutterBottom fontWeight="600">
                Error Distribution
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Breakdown of error types
              </Typography>
              
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={errorAnalysis}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ type, percentage }) => `${type}: ${percentage}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {errorAnalysis.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Error Details Table */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ 
              p: 3,
              bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
              border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`,
              height: '100%'
            }}>
              <Typography variant="h6" gutterBottom fontWeight="600">
                Error Analysis
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Detailed error statistics
              </Typography>

              <List>
                {errorAnalysis.map((error, index) => (
                  <React.Fragment key={index}>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ 
                          bgcolor: error.severity === 'high' ? 'error.main' : 
                                   error.severity === 'medium' ? 'warning.main' : 'info.main'
                        }}>
                          <ErrorIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body1" fontWeight="500">
                              {error.type}
                            </Typography>
                            <Chip 
                              label={error.count} 
                              size="small" 
                              color={getSeverityColor(error.severity)}
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <LinearProgress 
                              variant="determinate" 
                              value={error.percentage} 
                              sx={{ mt: 1, mb: 0.5 }}
                              color={getSeverityColor(error.severity)}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {error.percentage}% of total errors • Severity: {error.severity}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < errorAnalysis.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          </Grid>

          {/* Recent Executions */}
          <Grid item xs={12}>
            <Paper sx={{ 
              bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
              border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`
            }}>
              <Box sx={{ p: 2, borderBottom: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}` }}>
                <Typography variant="h6" fontWeight="600">
                  Recent Executions
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: isDarkMode ? '#2c2c2c' : '#f8f9fa' }}>
                    <TableRow>
                      <TableCell>Execution ID</TableCell>
                      <TableCell>Agent</TableCell>
                      <TableCell>Task</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Duration</TableCell>
                      <TableCell align="center">Accuracy</TableCell>
                      <TableCell align="center">API Calls</TableCell>
                      <TableCell align="center">Tokens</TableCell>
                      <TableCell>Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentExecutions.map((exec) => (
                      <TableRow key={exec.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {exec.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="500">
                            {exec.agent}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {exec.task}
                          </Typography>
                          {exec.error && (
                            <Typography variant="caption" color="error">
                              Error: {exec.error}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={exec.status}
                            size="small"
                            color={getStatusColor(exec.status)}
                            icon={exec.status === 'success' ? <SuccessIcon /> : <ErrorIcon />}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {exec.duration}s
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography 
                            variant="body2" 
                            fontWeight="500"
                            color={exec.accuracy >= 90 ? 'success.main' : 'text.primary'}
                          >
                            {exec.accuracy > 0 ? `${exec.accuracy}%` : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {exec.apiCalls}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {formatNumber(exec.tokensUsed)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(exec.startTime)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Code Metrics Tab */}
      {selectedTab === 4 && (
        <>
          {/* Code Quality Overview Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                height: '100%',
                bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
                border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`
              }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography color="text.secondary" variant="body2" gutterBottom>
                        Total Lines of Code
                      </Typography>
                      <Typography variant="h4" fontWeight="600">
                        {formatNumber(codeQualityOverview?.totalLinesOfCode || 0)}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                        {getTrendIcon(codeQualityOverview?.trendsVsPrevious?.linesOfCode)}
                        <Typography 
                          variant="caption" 
                          color={codeQualityOverview?.trendsVsPrevious?.linesOfCode > 0 ? 'success.main' : 'error.main'}
                        >
                          {Math.abs(codeQualityOverview?.trendsVsPrevious?.linesOfCode || 0)}% vs last period
                        </Typography>
                      </Box>
                    </Box>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                      <CodeIcon />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                height: '100%',
                bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
                border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`
              }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography color="text.secondary" variant="body2" gutterBottom>
                        Avg Test Coverage
                      </Typography>
                      <Typography variant="h4" fontWeight="600" color="success.main">
                        {codeQualityOverview?.avgTestCoverage || 0}%
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                        {getTrendIcon(codeQualityOverview?.trendsVsPrevious?.testCoverage)}
                        <Typography 
                          variant="caption" 
                          color={codeQualityOverview?.trendsVsPrevious?.testCoverage > 0 ? 'success.main' : 'error.main'}
                        >
                          {Math.abs(codeQualityOverview?.trendsVsPrevious?.testCoverage || 0)}% vs last period
                        </Typography>
                      </Box>
                    </Box>
                    <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}>
                      <VerifiedIcon />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                height: '100%',
                bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
                border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`
              }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography color="text.secondary" variant="body2" gutterBottom>
                        Code Quality Score
                      </Typography>
                      <Typography variant="h4" fontWeight="600" color="warning.main">
                        {codeQualityOverview?.avgCodeQuality || 0}/10
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                        {getTrendIcon(codeQualityOverview?.trendsVsPrevious?.codeQuality)}
                        <Typography 
                          variant="caption" 
                          color={codeQualityOverview?.trendsVsPrevious?.codeQuality > 0 ? 'success.main' : 'error.main'}
                        >
                          {Math.abs(codeQualityOverview?.trendsVsPrevious?.codeQuality || 0)}% vs last period
                        </Typography>
                      </Box>
                    </Box>
                    <Avatar sx={{ bgcolor: 'warning.main', width: 56, height: 56 }}>
                      <StarsIcon />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                height: '100%',
                bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
                border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`
              }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography color="text.secondary" variant="body2" gutterBottom>
                        Avg Complexity
                      </Typography>
                      <Typography variant="h4" fontWeight="600" color="info.main">
                        {codeQualityOverview?.avgComplexity || 0}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                        {getTrendIcon(-codeQualityOverview?.trendsVsPrevious?.complexity)}
                        <Typography 
                          variant="caption" 
                          color={codeQualityOverview?.trendsVsPrevious?.complexity < 0 ? 'success.main' : 'error.main'}
                        >
                          {Math.abs(codeQualityOverview?.trendsVsPrevious?.complexity || 0)}% vs last period
                        </Typography>
                      </Box>
                    </Box>
                    <Avatar sx={{ bgcolor: 'info.main', width: 56, height: 56 }}>
                      <SecurityIcon />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Lines of Code by Agent */}
          <Paper sx={{ 
            p: 3, 
            mb: 3,
            bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
            border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`
          }}>
            <Typography variant="h6" gutterBottom fontWeight="600">
              Lines of Code by Agent
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Code production comparison across agents
            </Typography>
            
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={codeMetrics}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="agent" />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Bar dataKey="linesOfCode" fill="#8884d8" name="Lines of Code" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Language Distribution */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ 
                p: 3,
                bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
                border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`,
                height: '100%'
              }}>
                <Typography variant="h6" gutterBottom fontWeight="600">
                  Language Distribution
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Code breakdown by programming language
                </Typography>
                
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={languageDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ language, percentage }) => `${language}: ${percentage}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="linesOfCode"
                    >
                      {languageDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>

            {/* Test Coverage Comparison */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ 
                p: 3,
                bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
                border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`,
                height: '100%'
              }}>
                <Typography variant="h6" gutterBottom fontWeight="600">
                  Test Coverage by Agent
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Testing quality across different agents
                </Typography>
                
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={codeMetrics} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" domain={[0, 100]} />
                    <YAxis dataKey="agent" type="category" width={150} />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="testCoverage" fill="#00C49F" name="Test Coverage %" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>

          {/* Code Quality Metrics Table */}
          <Paper sx={{ 
            bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
            border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`
          }}>
            <Box sx={{ p: 2, borderBottom: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}` }}>
              <Typography variant="h6" fontWeight="600">
                Detailed Code Quality Metrics
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: isDarkMode ? '#2c2c2c' : '#f8f9fa' }}>
                  <TableRow>
                    <TableCell>Agent</TableCell>
                    <TableCell align="center">Lines of Code</TableCell>
                    <TableCell align="center">Test Coverage</TableCell>
                    <TableCell align="center">Quality Score</TableCell>
                    <TableCell align="center">Complexity</TableCell>
                    <TableCell align="center">Maintainability</TableCell>
                    <TableCell align="center">Bugs</TableCell>
                    <TableCell align="center">Vulnerabilities</TableCell>
                    <TableCell align="center">Duplications</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {codeMetrics.map((metric) => (
                    <TableRow key={metric.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="500">
                          {metric.agent}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="500">
                          {formatNumber(metric.linesOfCode)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box>
                          <Typography 
                            variant="body2" 
                            fontWeight="600"
                            color={metric.testCoverage >= 80 ? 'success.main' : metric.testCoverage >= 60 ? 'warning.main' : 'error.main'}
                          >
                            {metric.testCoverage}%
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={metric.testCoverage} 
                            sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                            color={metric.testCoverage >= 80 ? 'success' : metric.testCoverage >= 60 ? 'warning' : 'error'}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={`${metric.codeQualityScore}/10`}
                          size="small"
                          sx={{ 
                            bgcolor: metric.codeQualityScore >= 8 ? 'success.main' : metric.codeQualityScore >= 6 ? 'warning.main' : 'error.main',
                            color: 'white'
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={metric.cyclomaticComplexity}
                          size="small"
                          color={getComplexityColor(metric.cyclomaticComplexity)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box>
                          <Typography variant="body2" fontWeight="500">
                            {metric.maintainabilityIndex}
                          </Typography>
                          <LinearProgress 
                            variant="determinate" 
                            value={metric.maintainabilityIndex} 
                            sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                            color="primary"
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={metric.bugs}
                          size="small"
                          color={metric.bugs < 10 ? 'success' : metric.bugs < 20 ? 'warning' : 'error'}
                          icon={<BugIcon />}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={metric.vulnerabilities}
                          size="small"
                          color={metric.vulnerabilities < 2 ? 'success' : metric.vulnerabilities < 5 ? 'warning' : 'error'}
                          icon={<SecurityIcon />}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" color={metric.duplications < 3 ? 'success.main' : 'warning.main'}>
                          {metric.duplications}%
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Code Quality Trends */}
          <Paper sx={{ 
            p: 3, 
            mt: 3,
            bgcolor: isDarkMode ? '#1e1e1e' : '#ffffff',
            border: `1px solid ${isDarkMode ? '#333' : '#e9ecef'}`
          }}>
            <Typography variant="h6" gutterBottom fontWeight="600">
              Code Quality vs Complexity
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Relationship between code quality scores and cyclomatic complexity
            </Typography>
            
            <ResponsiveContainer width="100%" height={350}>
              <ComposedChart data={codeMetrics}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="agent" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <RechartsTooltip />
                <Legend />
                <Bar 
                  yAxisId="left"
                  dataKey="codeQualityScore" 
                  fill="#8884d8" 
                  name="Quality Score"
                />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="cyclomaticComplexity" 
                  stroke="#FF8042" 
                  strokeWidth={2}
                  name="Complexity"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default AgentPerformanceMetrics;